<template>
    <div
        ref="root"
        class="multiselect"
        :class="{
            'multiselect--disabled': disabled,
            'multiselect--open': isOpen,
            'multiselect--active': isOpen,
        }"
    >
        <div class="multiselect__tags" @click.stop="toggle">
            <input
                v-if="isOpen && searchable"
                type="text"
                v-model="internalSearch"
                ref="searchInput"
                class="multiselect__input"
                :placeholder="placeholder"
                @input="onSearchInput"
            />
            <span v-else-if="!isOpen" class="multiselect__placeholder">
                {{ placeholder }}
            </span>
            <!-- Стрелка вниз -->
            <div class="multiselect__select"></div>
        </div>

        <transition name="multiselect">
            <div
                v-show="isOpen"
                class="multiselect__content-wrapper"
                @click.stop
            >
                <div class="multiselect__list">
                    <div
                        v-for="option in filteredOptions"
                        :key="option.value"
                        class="list-group-item"
                    >
                        <b-form-checkbox v-model="option.active">{{
                            option.label
                        }}</b-form-checkbox>
                    </div>
                    <div v-if="!filteredOptions.length" class="list-group-item">
                        Нет результатов
                    </div>
                </div>

                <div class="multiselect__actions">
                    <b-button
                        size="sm"
                        variant="primary"
                        class="mr-2"
                        @click.stop="applySelection"
                    >
                        {{ $t("modules.common.select") }}
                    </b-button>
                    <b-button
                        size="sm"
                        variant="secondary"
                        @click.stop="cancelSelection"
                    >
                        {{ $t("modules.common.cancel") }}
                    </b-button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: "CheckboxMultiselect",
    props: {
        options: { type: Array, required: true },
        placeholder: { type: String, default: "Выберите элемент" },
        searchable: { type: Boolean, default: true },
        multiple: { type: Boolean, default: true },
        maxHeight: { type: Number, default: 300 },
        value: { type: Array, default: () => [] },
        disabled: { type: Boolean, default: false },
        openDirection: { type: String, default: "" },
    },
    data() {
        return {
            isOpen: false,
            internalSearch: "",
            internalOptions: this.options.map((o) => ({
                ...o,
                active: this.value.includes(o.value),
            })),
            backupOptions: [], // для отката
        };
    },
    computed: {
        filteredOptions() {
            if (!this.internalSearch) return this.internalOptions;
            return this.internalOptions.filter((o) =>
                o.label
                    .toLowerCase()
                    .includes(this.internalSearch.toLowerCase())
            );
        },
        selectedValues() {
            return this.internalOptions.filter((o) => o.active);
        },
    },
    watch: {
        options(newOptions) {
            this.internalOptions = newOptions.map((o) => {
                const existing = this.internalOptions.find(
                    (i) => i.value === o.value
                );
                return { ...o, active: existing ? existing.active : false };
            });
        },
        value(newValue) {
            if (!Array.isArray(newValue)) {
                newValue = [];
            }
            this.internalOptions = this.internalOptions.map((o) => ({
                ...o,
                active: newValue.includes(o.value),
            }));
        },
        disabled(val) {
            if (val) {
                this.isOpen = false;
                this.internalSearch = "";
            }
        },
    },
    mounted() {
        document.addEventListener("mousedown", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("mousedown", this.handleClickOutside);
    },

    methods: {
        toggle() {
            if (this.disabled) return;
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.backupOptions = this.internalOptions.map((o) => ({
                    ...o,
                }));
                this.$nextTick(() => this.$refs.searchInput?.focus());
            }
        },
        onSearchInput() {
            this.$emit("search-change", this.internalSearch);
        },
        applySelection() {
            this.$emit(
                "input",
                this.selectedValues.map((o) => o.value)
            );

            this.isOpen = false;
        },
        cancelSelection() {
            this.internalOptions = this.backupOptions.map((o) => ({ ...o }));
            this.isOpen = false;
        },
        handleClickOutside(e) {
            if (!this.isOpen) return;

            const root = this.$refs.root;
            if (!root) return;

            // клик внутри компонента
            if (root.contains(e.target)) return;

            // клик вне
            this.isOpen = false;
            this.internalOptions = this.backupOptions.map((o) => ({ ...o }));
            this.internalSearch = "";
        },
    },
};
</script>

<style scoped>
.multiselect__content-wrapper {
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
    margin-top: 2px;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-height: 350px;
}
.multiselect__list {
    max-height: 300px;
    overflow-y: auto;
}
.multiselect__actions {
    padding: 4px;
    border-top: 1px solid #eee;
    display: flex;
    justify-content: flex-end;
    background: #f9f9f9;
}
/* текст элементов списка */
::v-deep .list-group-item {
    font-size: 13px !important;
    padding: 4px 8px !important;
}

/* размер чекбокса */
::v-deep .custom-control-label::before,
::v-deep .custom-control-label::after {
    width: 15px !important;
    height: 15px !important;
    top: 2px !important;
}

/* размер текста элементов*/
::v-deep .custom-control-label {
    padding-top: 3px !important;
    font-size: 13px !important;
    font-weight: 400 !important;
}

/* кнопки внутри multiselect */
::v-deep .multiselect__actions .btn {
    padding: 2px 8px !important;
    font-size: 11px !important;
    line-height: 1.2 !important;
    height: 25px !important;
}
.multiselect--disabled .multiselect__tags {
    background-color: rgb(248, 249, 252);
    color: rgb(71, 83, 101);
    cursor: not-allowed;
}

.multiselect--disabled .multiselect__input {
    background-color: rgb(248, 249, 252);
    color: rgb(71, 83, 101);
    cursor: not-allowed;
}
.multiselect .multiselect__select:before {
    content: "\ea23";
    margin: 0;
    top: 2px;
    border-color: transparent;
    border-width: 0;
    color: #7c9db3;
}
.multiselect__select:before {
    position: relative;
    right: 0;
    top: 65%;
    color: #999;
    margin-top: 4px;
    border-color: #999 transparent transparent;
    border-style: solid;
    border-width: 5px 5px 0;
    content: "";
}
.multiselect--open .multiselect__select:before {
    content: "\ea32";
    color: #7c9db3;
}
</style>
